import { computed, inject, Injectable, type Signal } from '@angular/core';
import { select } from '@ngxs/store';

import { ProjectDetailSelectors } from '@efficiently/client-domain-project-detail';

import { PlatformAdminTabsStore } from '@ds/pages/platform-admin/platform-admin-tabs.store';
import { ProjectNavigatorStore } from '@ds/pages/projects/navigator/project-navigator.store';
import { PLATFORM_ADMIN_LABEL } from '@ds/shared/constants/platform-admin-tabs.constant';

/**
 * The one answer to "what is the header's title right now" — the Platform Administrator label inside
 * that group, the current project's name inside a project, and nothing anywhere else.
 *
 * **Why a service and not a computed on the header.** The two sources sit on opposite sides of the
 * component tree from the header: {@link PlatformAdminTabsStore} is written from inside a lazily
 * loaded route subtree, and the project's identity is split between the router (the navigator) and
 * NGXS (what is actually loaded). Collecting them here keeps the precedence in one readable place
 * instead of spreading it across a template, and gives phase 2's header a plain `Signal` to bind.
 *
 * `providedIn: 'root'` for the same reason the two stores carry it: every source it reads is a root
 * singleton, and a service provided on the header component would be rebuilt with the header.
 *
 * **It reports WHERE THE USER IS, never what the user MAY DO.** Nothing here is an authorization
 * signal — `isActive` in particular says the Platform Administrator group is open, not that the
 * session may do anything inside it. The `canMatch` permission guards on the routes remain the only
 * gate, and no caller may read this title as one.
 */
@Injectable({ providedIn: 'root' })
export class HeaderContextTitleService {
	private readonly platformAdminTabsStore = inject(PlatformAdminTabsStore);

	private readonly projectNavigatorStore = inject(ProjectNavigatorStore);

	/**
	 * The project NGXS currently holds, and its name.
	 *
	 * Initialized as FIELDS, which is the only correct place for them: `select()` injects the store
	 * implicitly, so it needs an injection context. Called from inside the `computed` below — or from
	 * a `setTimeout`, a `Promise.then` or module scope — it throws at runtime, and no compiler, lint
	 * rule or test in this repository catches it. The signals are read inside the `computed`; the
	 * `select()` calls stay here.
	 */
	private readonly loadedProjectId = select<string>(ProjectDetailSelectors.projectId);

	private readonly loadedProjectName = select<string>(ProjectDetailSelectors.projectName);

	/**
	 * The header's title, by first match: the admin label, then the active project's name, then
	 * nothing.
	 */
	public readonly title: Signal<string | null> = computed(() => {
		if (this.platformAdminTabsStore.isActive()) {
			return PLATFORM_ADMIN_LABEL;
		}

		const activeProjectId = this.projectNavigatorStore.activeProjectId();

		if (activeProjectId === null) {
			return null;
		}

		// The two ids come from different places and disagree for a moment on every project switch:
		// the navigator's comes from the URL and moves as soon as the route does, while the NGXS one
		// is what has finished LOADING. Rendering the name without this check shows the previous
		// project's name over the new project's page for the length of that load — which is the bug
		// this guard exists to prevent, not a formality. Reading the id below is also what keeps the
		// project-detail selectors out of every non-project page: outside a project there is no
		// active id, and the early return above means neither selector signal is ever read there.
		if (activeProjectId !== this.loadedProjectId()) {
			return null;
		}

		const loadedProjectName = this.loadedProjectName();

		// An unnamed or not-yet-populated project yields no title at all. The header renders an empty
		// heading otherwise, which reads as a layout defect rather than as "nothing to show here".
		if (loadedProjectName.length === 0) {
			return null;
		}

		return loadedProjectName;
	});
}
